import { useMemo } from 'react';
import { COURSE_TRANSLATION_LANGUAGES } from '@duke-hq/shared-constants/courseTranslationLanguages';
import {
  deriveTranslationStatus,
  type ContentTranslationUiStatus,
} from '@duke-hq/shared-constants/contentTranslationStatus';
import {
  trpcAdmin,
  type RouterInputs,
  type RouterOutputs,
} from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  aggregateStatuses,
  countByBucket,
  TRANSLATION_POLL_INTERVAL_MS,
  type CoverageStatus,
} from './coverageStatus';

// Stop the error-driven poll after this many consecutive failures: past here the
// backend is likely genuinely broken, not blipping, so a manual refresh is better
// than a permanent heartbeat.
const MAX_ERROR_POLLS = 5;

// Derived from the query input so it stays in step with the backend
// (CONVERSATION | PAGE) without a hand-maintained union.
export type TranslatableResourceKind =
  RouterInputs['contentTranslations']['listByResourceVersion']['resourceKind'];

type ListByResourceVersionOutput = RouterOutputs['contentTranslations']['listByResourceVersion'];
type ContentTranslationRow = ListByResourceVersionOutput['rows'][number];

// Covers all target languages, even those with no row yet (`not-translated`), so
// the panel lists every language the course can be translated into.
export type LanguageTranslation = {
  code: string;
  englishName: string;
  endonym: string;
  status: ContentTranslationUiStatus;
  row: ContentTranslationRow | undefined;
  // Whether a course containing this resource actually offers the language.
  // The panel shows every translatable language but greys the un-offered ones.
  isOffered: boolean;
};

export type TranslationSummaryCounts = {
  ready: number;
  pending: number;
  failed: number;
  stale: number;
  notTranslated: number;
  total: number;
};

// The shared CoverageStatus (so the pill reads like every other surface), plus an
// `unknown` fallback so a failed status load never shows a bogus "all translated".
export type TranslationPillSummary = CoverageStatus | { kind: 'unknown' };

function summarise(languages: LanguageTranslation[]): {
  counts: TranslationSummaryCounts;
  pill: TranslationPillSummary;
} {
  const bucket = countByBucket(languages.map((language) => language.status));
  // This shape calls it `pending`; the shared bucket calls it `inProgress`.
  const counts: TranslationSummaryCounts = {
    ready: bucket.ready,
    pending: bucket.inProgress,
    failed: bucket.failed,
    stale: bucket.stale,
    notTranslated: bucket.notTranslated,
    total: bucket.total,
  };
  return { counts, pill: aggregateStatuses(languages.map((language) => language.status)) };
}

// Maps each target language to its derived UI status and returns summary counts
// plus a one-line pill summary for the toolbar.
export function useContentTranslations({
  resourceVersionId,
  resourceKind,
  enabled = true,
  isPanelOpen = false,
}: {
  resourceVersionId: number;
  resourceKind: TranslatableResourceKind;
  enabled?: boolean;
  // Polls regardless of current state while open, so a just-published resource
  // discovers its freshly-created rows: the pre-publish fetch had zero rows, so a
  // pending-only poll would never start and the panel would sit on stale empty data.
  isPanelOpen?: boolean;
}) {
  const query = trpcAdmin.contentTranslations.listByResourceVersion.useQuery(
    { resourceVersionId, resourceKind },
    {
      enabled,
      // Poll while open, or while closed if anything is still PENDING (so the
      // toolbar dot keeps up in the background). On a transient load error keep
      // polling so it self-heals, but give up after a run of consecutive failures
      // so a genuinely broken backend doesn't become a permanent heartbeat.
      refetchInterval: (query) => {
        const hasPending = query.state.data?.rows.some((row) => row.status === 'PENDING') ?? false;
        const keepRetrying =
          query.state.status === 'error' && query.state.fetchFailureCount < MAX_ERROR_POLLS;
        return isPanelOpen || hasPending || keepRetrying ? TRANSLATION_POLL_INTERVAL_MS : false;
      },
      // Keep polling when the tab is backgrounded: the author often publishes,
      // switches to the student preview to watch the result, then comes back.
      refetchIntervalInBackground: true,
    },
  );

  const currentPublishedVersionId = query.data?.currentPublishedVersionId ?? null;

  // Which languages the resource's courses actually offer (union of their
  // configs). The panel still lists all translatable languages, greying any not
  // offered by a containing course.
  const offeredQuery = trpcAdmin.contentTranslations.getResourceOfferedLanguages.useQuery(
    { resourceVersionId },
    { enabled },
  );

  const languages = useMemo<LanguageTranslation[]>(() => {
    const rowsByLanguage = new Map<string, ContentTranslationRow>(
      (query.data?.rows ?? []).map((row) => [row.language, row]),
    );
    const offered = new Set(offeredQuery.data?.languages ?? []);

    return COURSE_TRANSLATION_LANGUAGES.map((language) => {
      const row = rowsByLanguage.get(language.code);
      return {
        code: language.code,
        englishName: language.englishName,
        endonym: language.endonym,
        status: deriveTranslationStatus(row, currentPublishedVersionId),
        row,
        isOffered: offered.has(language.code),
      };
    });
  }, [query.data?.rows, currentPublishedVersionId, offeredQuery.data?.languages]);

  const { counts, pill: summaryPill } = useMemo(() => summarise(languages), [languages]);

  // On a failed load there's nothing to summarise, so the pill drops to `unknown`
  // rather than the bogus "all available" an empty language set would produce.
  const pill: TranslationPillSummary = query.isError ? { kind: 'unknown' } : summaryPill;

  // Most recent activity across all rows, for the panel's "Last updated" footer.
  const lastUpdatedAt = useMemo<Date | null>(() => {
    const timestamps = (query.data?.rows ?? [])
      .map((row) => row.generatedAt ?? row.updatedAt)
      .filter((value): value is Date => value != null);
    if (timestamps.length === 0) {
      return null;
    }
    return timestamps.reduce((latest, value) => (value > latest ? value : latest));
  }, [query.data?.rows]);

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    languages,
    counts,
    pill,
    lastUpdatedAt,
  };
}
